import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  unattendedCount: 0,
  // AIVIO: account-wide unattended count for the sidebar "Неотвеченные" item.
  // Kept SEPARATE from unattendedCount, which is scoped to the current list
  // view (per-inbox). The sidebar item is account-wide, so it needs its own
  // value that scoped list fetches do not clobber.
  accountUnattendedCount: 0,
  // AIVIO: вкладка «Бот передал» — счётчик и флаг «в области запроса есть
  // активный бот» (оба из пропатченного meta; см. conversation_finder.rb).
  botHandoffCount: 0,
  botHandoffEnabled: false,
  // AIVIO: пункт бокового меню «Бот передал» — по всему аккаунту (как
  // accountUnattendedCount): счётчик и «у пользователя есть активный бот».
  accountBotHandoffCount: 0,
  accountBotHandoffEnabled: false,
};

// Do not keep polling Rails from background tabs. Vuex actions can continue to
// be dispatched by websocket events while the page is hidden, so retain only
// the latest request (with its view token) and refresh once when the operator
// returns.
let pendingMetaRequest = null;
let pendingAccountUnattendedCommit = null;

const isPageHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

const redirectIfUnauthorized = error => {
  if (error?.response?.status === 401) {
    window.location.href = '/app/login';
  }
};

export const getters = {
  getStats: $state => $state,
  // AIVIO
  getAccountUnattendedCount: $state => $state.accountUnattendedCount,
  getAccountBotHandoffCount: $state => $state.accountBotHandoffCount,
  getAccountBotHandoffEnabled: $state => $state.accountBotHandoffEnabled,
};

// Upstream 16771f6ed: counts are shared across assignee tabs, pages, and sort
// orders, so a view is identified only by the filters that change the counts.
// AIVIO: conversationType also carries our "Неотвеченные" tab (unattended).
const getViewKey = ({
  inboxId,
  status,
  labels,
  teamId,
  conversationType,
  queryData,
} = {}) =>
  JSON.stringify([
    ConversationApi.url,
    queryData || {
      inboxId,
      status,
      labels,
      teamId,
      conversationType,
    },
  ]);

let activeView = { key: null, params: {}, committedRequestId: 0 };
let requestId = 0;

// A refresh may only commit while its view is still shown and nothing newer
// has been committed for that view. This stops a debounced (7.5-30 s) or slow
// response of the previous view from overwriting the current view's counts.
const isCurrentRequest = request =>
  !!request &&
  request.view === activeView &&
  request.id >= activeView.committedRequestId;

const commitCounts = (commit, meta, request) => {
  if (!isCurrentRequest(request)) return;

  activeView.committedRequestId = request.id;
  commit(types.SET_CONV_TAB_META, meta);
};

const fetchMetaData = async (commit, params, request) => {
  if (!isCurrentRequest(request)) return;

  if (isPageHidden()) {
    pendingMetaRequest = { commit, params, request };
    return;
  }

  try {
    const response = params.queryData
      ? await ConversationApi.filter({ queryData: params.queryData, page: 1 })
      : await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    // AIVIO: флаг «Бот передал» берём только из ответа списка (текущий
    // источник). Отложенный meta мог уйти для прежнего источника — не даём
    // ему спрятать или показать вкладку.
    const { bot_handoff_enabled: staleScopeFlag, ...scopedMeta } = meta || {};
    commitCounts(commit, scopedMeta, request);
  } catch (error) {
    redirectIfUnauthorized(error);
    // ignore other errors
  }
};

// Upstream 03fb6591e + ab01ab785 (v4.14.1/v4.16.0): slower tiers, and an
// unknown count (0 until a meta request succeeds) polls at the slowest tier.
const debouncedFetchMetaData = debounce(fetchMetaData, 1000, false, 5000);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 7500, false, 20000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  15000,
  false,
  30000
);

const metaDebouncers = {
  default: debouncedFetchMetaData,
  long: longDebouncedFetchMetaData,
  superLong: superLongDebouncedFetchMetaData,
};

// allCount is 0 until a meta request succeeds; under load it stays 0, so treat
// the unknown case as a large account and poll slowest instead of fastest.
export const getMetaDebounceKey = allCount => {
  if (allCount > 2000 || allCount === 0) return 'superLong';
  if (allCount > 100) return 'long';
  return 'default';
};

// AIVIO: account-wide unattended count (no inbox scope) for the sidebar badge.
// Hits the dedicated meta endpoint (returns unattended_count via the patched
// meta.json.jbuilder) and stores it in a separate field so scoped list-meta
// commits never overwrite it. It is not tied to a list view, so it only needs
// response ordering: an older, slower response must not replace a newer count.
let accountUnattendedRequestId = 0;
let accountUnattendedCommittedId = 0;

export const fetchAccountUnattended = async (
  commit,
  { fresh = false } = {}
) => {
  if (isPageHidden()) {
    pendingAccountUnattendedCommit = commit;
    return;
  }

  accountUnattendedRequestId += 1;
  const currentRequestId = accountUnattendedRequestId;
  try {
    // status: 'open' — match the native sidebar "Неотвеченные" view (open
    // unattended only). 'all' would inflate by counting resolved/pending too.
    const metaParams = { status: 'open', assigneeType: 'all' };
    if (fresh) metaParams.fresh = true;
    const {
      data: { meta },
    } = await ConversationApi.meta(metaParams);
    if (currentRequestId < accountUnattendedCommittedId) return;

    accountUnattendedCommittedId = currentRequestId;
    commit('SET_ACCOUNT_UNATTENDED_COUNT', meta?.unattended_count ?? 0);
    // AIVIO: тот же ответ несёт счётчик и флаг «Бот передал» для бокового меню.
    commit('SET_ACCOUNT_BOT_HANDOFF', {
      count: meta?.bot_handoff_count ?? 0,
      enabled: Boolean(meta?.bot_handoff_enabled),
    });
  } catch (error) {
    redirectIfUnauthorized(error);
    // ignore other errors
  }
};
// AIVIO: was 500ms/5s (up to 12 extra meta calls/min per tab on busy
// accounts). Same cadence as the upstream "long" tier: at most ~3/min per tab.
const debouncedFetchAccountUnattended = debounce(
  fetchAccountUnattended,
  7500,
  false,
  20000
);

// AIVIO: сразу после действия самого оператора (завершил, отложил, ответил,
// назначил) — свежие счётчики: вкладки текущего вида и боковое меню
// («Неотвеченные», «Бот передал»). Без дебаунса (7.5-30 с) и без 10-секундного
// кэша сервера (fresh=1 пересчитывает и обновляет кэш для остальных запросов).
// Не чаще раза в секунду: серия действий подряд даёт один запрос в конце.
export const REFRESH_NOW_MIN_INTERVAL_MS = 1000;
let lastRefreshNowAt = 0;
let refreshNowTimer = null;

const runRefreshNow = commit => {
  refreshNowTimer = null;
  lastRefreshNowAt = Date.now();
  if (activeView.key) {
    requestId += 1;
    const request = { view: activeView, id: requestId };
    fetchMetaData(commit, { ...activeView.params, fresh: true }, request);
  }
  fetchAccountUnattended(commit, { fresh: true });
};

const refreshAfterTabBecomesVisible = () => {
  if (isPageHidden()) return;

  const metaRequest = pendingMetaRequest;
  const accountUnattendedCommit = pendingAccountUnattendedCommit;
  pendingMetaRequest = null;
  pendingAccountUnattendedCommit = null;

  // Промисы намеренно не ждём: оба запроса сами глотают ошибки.
  if (metaRequest) {
    // Dropped inside fetchMetaData if the view changed while the tab was hidden.
    fetchMetaData(metaRequest.commit, metaRequest.params, metaRequest.request);
  }
  if (accountUnattendedCommit) {
    fetchAccountUnattended(accountUnattendedCommit);
  }
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', refreshAfterTabBecomesVisible);
}

export const actions = {
  get: ({ commit, state: $state }, params = activeView.params) => {
    // Upstream 16771f6ed: refreshes for a view that is no longer shown are
    // ignored instead of being queued behind the debounce.
    if (getViewKey(params) !== activeView.key) return;

    requestId += 1;
    const request = { view: activeView, id: requestId };
    if (isPageHidden()) {
      pendingMetaRequest = { commit, params, request };
      return;
    }

    metaDebouncers[getMetaDebounceKey($state.allCount)](
      commit,
      params,
      request
    );
  },
  onListRequestStarted(_, params) {
    const key = getViewKey(params);
    if (key !== activeView.key) {
      activeView = { key, params, committedRequestId: 0 };
    }
    requestId += 1;
    return { view: activeView, id: requestId };
  },
  set({ commit }, { meta, request } = {}) {
    commitCounts(commit, meta, request);
  },
  // AIVIO: см. runRefreshNow — после действия самого оператора.
  refreshNow({ commit }) {
    if (isPageHidden() || refreshNowTimer) return;
    const wait = lastRefreshNowAt + REFRESH_NOW_MIN_INTERVAL_MS - Date.now();
    if (wait > 0) {
      refreshNowTimer = setTimeout(() => runRefreshNow(commit), wait);
      return;
    }
    runRefreshNow(commit);
  },
  // AIVIO: refresh the account-wide unattended count (sidebar badge).
  getAccountWide({ commit }) {
    if (isPageHidden()) {
      pendingAccountUnattendedCommit = commit;
      return;
    }

    debouncedFetchAccountUnattended(commit);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
      unattended_count: unattendedCount,
      bot_handoff_count: botHandoffCount,
      bot_handoff_enabled: botHandoffEnabled,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    // AIVIO: only the patched conversations meta endpoint sends unattended_count.
    // List/filter/search meta payloads omit it — keep last-known value instead of
    // clobbering to undefined (which made the badge flicker/disappear on navigation).
    if (unattendedCount !== undefined) {
      $state.unattendedCount = unattendedCount;
    }
    // AIVIO: «Бот передал» — как unattended: ключи шлёт только пропатченный
    // meta; ответы фильтров/папок/поиска их не содержат и не должны сбрасывать
    // счётчик и прятать вкладку.
    if (botHandoffCount !== undefined) {
      $state.botHandoffCount = botHandoffCount;
    }
    if (botHandoffEnabled !== undefined) {
      $state.botHandoffEnabled = botHandoffEnabled;
    }
    $state.updatedOn = new Date();
  },
  // AIVIO: account-wide unattended count (sidebar badge). Literal string key
  // to avoid touching the shared mutation-types file.
  SET_ACCOUNT_UNATTENDED_COUNT($state, count) {
    $state.accountUnattendedCount = count || 0;
  },
  // AIVIO: пункт бокового меню «Бот передал».
  SET_ACCOUNT_BOT_HANDOFF($state, { count = 0, enabled = false } = {}) {
    $state.accountBotHandoffCount = count || 0;
    $state.accountBotHandoffEnabled = Boolean(enabled);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
